import { WorkOrderTaskTypeGroup } from '@xbcb/ui-types';
import {
  EuCustomsEntryExceptionTaskType,
  WorkOrderTaskStatus,
  WorkOrderTaskType,
} from '@xbcb/work-order-types';
import { DocumentTag } from '@xbcb/document-types';
import {
  WorkOrder,
  DocumentTag as apiGDocumentTag,
} from '@xbcb/api-gateway-client';
import { TagKey, TagValue } from '@xbcb/shared-types';
import { findMatchingWorkOrderTask } from 'libs/findMatchingWorkOrderTask';
import { determineWorkOrderTaskTypeGroup } from 'libs/workOrderTaskTypeGroups';
import { WorkOrderTaskEvaluatedState } from 'libs/evaluateWorkOrderTaskState';

export const findWorkOrderTaskAndTaskTypeGroup = (
  workOrder: WorkOrder,
  workOrderTaskType: WorkOrderTaskType,
) => {
  const workOrderTask = findMatchingWorkOrderTask(workOrder, workOrderTaskType);
  const workOrderTaskTypeGroup =
    determineWorkOrderTaskTypeGroup(workOrderTaskType);
  return { workOrderTask, workOrderTaskTypeGroup };
};

// work orders contain all the documentTags specified
const areDocumentsPresentOnWorkOrder = (
  workOrder: WorkOrder,
  documentTags: apiGDocumentTag[],
) =>
  documentTags.every((documentTag) =>
    (workOrder.group?.documents || [])
      .filter(({ deleted }) => !deleted)
      .map(({ documentTags }) => documentTags)
      .flat()
      .includes(documentTag),
  );

export const showWorkOrderTaskCompletionButton = (
  workOrder: WorkOrder,
  workOrderTaskType?: WorkOrderTaskType,
  workOrderTaskEvaluatedState?: WorkOrderTaskEvaluatedState,
) => {
  if (!workOrderTaskType) return false;
  const { workOrderTask, workOrderTaskTypeGroup } =
    findWorkOrderTaskAndTaskTypeGroup(workOrder, workOrderTaskType);
  const containsSystemOnlySteps =
    workOrderTaskTypeGroup === WorkOrderTaskTypeGroup.SYSTEM_ONLY;
  const isCompletedOrCanceled = [
    WorkOrderTaskStatus.COMPLETED,
    WorkOrderTaskStatus.CANCELED,
  ].includes(workOrderTask?.status);

  const is1PEuBroker = workOrder.group.customsBroker?.tags.some(
    ({ key, value }) =>
      key === TagKey.STRATEGIC_BUSINESS_UNIT &&
      value === TagValue.WONDERWALL_BROKER,
  );
  const documentPresentOnWorkOrder = areDocumentsPresentOnWorkOrder(workOrder, [
    DocumentTag.PACKING_LIST,
    DocumentTag.APPROVED_DOCUMENT,
  ]);
  const EuDocumentsCheck = is1PEuBroker ? documentPresentOnWorkOrder : true;
  // TODO currently we are maintaining this list but in the future, we should get this information
  // from task config
  const shouldSkipSubStatusCheck = [
    WorkOrderTaskType.GB_CUSTOMS_ENTRY_REVIEW_DOCUMENTS,
    WorkOrderTaskType.DE_CUSTOMS_ENTRY_REVIEW_DOCUMENTS,
    WorkOrderTaskType.NL_CUSTOMS_ENTRY_REVIEW_DOCUMENTS,
    WorkOrderTaskType.FR_CUSTOMS_ENTRY_REVIEW_DOCUMENTS,
    ...Object.keys(EuCustomsEntryExceptionTaskType),
  ].includes(workOrderTaskType);
  const isActivelyInProgress =
    workOrderTaskEvaluatedState === WorkOrderTaskEvaluatedState.STARTED;
  return (
    EuDocumentsCheck &&
    !isCompletedOrCanceled &&
    !containsSystemOnlySteps &&
    (shouldSkipSubStatusCheck || isActivelyInProgress)
  );
};

export const disableWorkOrderTaskCompletionButton = (
  workOrder: WorkOrder,
  workOrderTaskType: WorkOrderTaskType,
) => {
  const { workOrderTask, workOrderTaskTypeGroup } =
    findWorkOrderTaskAndTaskTypeGroup(workOrder, workOrderTaskType);
  const containsOptionalSteps =
    workOrderTaskTypeGroup === WorkOrderTaskTypeGroup.STEPS_OPTIONAL;
  // enable the button if the task does not have any required/system-only steps
  if (containsOptionalSteps) return false;
  // enable the button only if all final/non-system only steps have been completed
  return workOrderTask.steps.reduce((incompleteTasks: boolean, step: any) => {
    if (incompleteTasks || step.isFinalConfirmation) return incompleteTasks;
    if (!step.completed && !step.isCompletedBySystem) return true;
    return false;
  }, false);
};
